// src/components/roleplay/ScenarioSetup.js
// Step 1 of a practice session: pick a Track, a Persona (filtered by track),
// and a Difficulty, then start the call.
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { TRACKS, DIFFICULTIES } from '../../config/roleplay';

const Section = styled.div`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px;
`;

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

const Tile = styled.button`
  appearance: none;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${(props) => (props.$active ? '#e6f5f0' : '#ffffff')};
  border: 1px solid ${(props) => (props.$active ? '#0e8c6a' : '#e2e8f0')};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #0e8c6a;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06);
  }
`;

const TileTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.$active ? '#0e8c6a' : '#1e293b')};
`;

const TileSub = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const TileMeta = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const EmptyState = styled.div`
  color: #64748b;
  font-size: 14px;
  padding: 24px;
  text-align: center;
  background: #ffffff;
  border: 1px dashed #e2e8f0;
  border-radius: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const StartButton = styled.button`
  appearance: none;
  border: none;
  background: #0e8c6a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  padding: 12px 22px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;

  &:hover {
    background: #0b7057;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function ScenarioSetup({ personas, onStart }) {
  const [track, setTrack] = useState(TRACKS[0]?.key || '');
  const [personaId, setPersonaId] = useState('');
  const [difficulty, setDifficulty] = useState(DIFFICULTIES[1]?.key || DIFFICULTIES[0]?.key || '');

  const visiblePersonas = useMemo(
    () => (personas || []).filter((p) => p.track === track && !p.archived),
    [personas, track]
  );

  // Keep a valid persona selected: default to the first one for the current
  // track (so a single-persona track needs no extra click), and clear/reselect
  // when the track changes or personas load in.
  useEffect(() => {
    setPersonaId((current) =>
      visiblePersonas.some((p) => p.id === current) ? current : visiblePersonas[0]?.id || ''
    );
  }, [visiblePersonas]);

  function handleStart() {
    if (!personaId) return;
    onStart({ personaId, track, difficulty });
  }

  return (
    <div>
      <Section>
        <SectionTitle>1. Choose a track</SectionTitle>
        <TileGrid>
          {TRACKS.map((t) => (
            <Tile key={t.key} type="button" $active={track === t.key} onClick={() => setTrack(t.key)}>
              <TileTitle $active={track === t.key}>{t.label}</TileTitle>
              <TileSub>{t.description}</TileSub>
            </Tile>
          ))}
        </TileGrid>
      </Section>

      <Section>
        <SectionTitle>2. Choose a persona</SectionTitle>
        {visiblePersonas.length === 0 ? (
          <EmptyState>No personas for this track yet. Ask an admin to add one.</EmptyState>
        ) : (
          <TileGrid>
            {visiblePersonas.map((p) => (
              <Tile
                key={p.id}
                type="button"
                $active={personaId === p.id}
                onClick={() => setPersonaId(p.id)}
              >
                <TileMeta>{p.company || 'Prospect'}</TileMeta>
                <TileTitle $active={personaId === p.id}>{p.title}</TileTitle>
                <TileSub>{(p.brief || '').slice(0, 110)}{(p.brief || '').length > 110 ? '…' : ''}</TileSub>
              </Tile>
            ))}
          </TileGrid>
        )}
      </Section>

      <Section>
        <SectionTitle>3. Choose a difficulty</SectionTitle>
        <TileGrid>
          {DIFFICULTIES.map((d) => (
            <Tile
              key={d.key}
              type="button"
              $active={difficulty === d.key}
              onClick={() => setDifficulty(d.key)}
            >
              <TileTitle $active={difficulty === d.key}>{d.label}</TileTitle>
              <TileSub>{d.blurb}</TileSub>
            </Tile>
          ))}
        </TileGrid>
      </Section>

      <Footer>
        <StartButton type="button" disabled={!personaId} onClick={handleStart}>
          Start call
        </StartButton>
      </Footer>
    </div>
  );
}

export default ScenarioSetup;
